import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import WorldMap from './WorldMap';

interface Location {
  country: string;
  flag: string;
  views: number;
}

interface HubGeographicDistributionProps {
  locationsData: Location[];
  hubStats?: Array<{
    name: string;
    country: string;
    dataCentre: string;
    views: number;
    projectCount: number;
    accMemberCount: number;
    hubAppUserCount: number;
  }>;
}

const HubGeographicDistribution: React.FC<HubGeographicDistributionProps> = ({ locationsData, hubStats = [] }) => {
  const [activeTab, setActiveTab] = useState('hubs');
  const [showFilters, setShowFilters] = useState(false);

  // 与World Map使用相同的颜色计算逻辑
  const getBarColor = (views: number, data: Array<{views: number}>) => {
    const maxViews = Math.max(...data.map(item => item.views));
    const minViews = Math.min(...data.map(item => item.views));
    
    let intensity;
    let hue;
    
    // 处理当所有数据都相同时的情况（避免除以0导致的NaN）
    if (maxViews === minViews) {
      // 如果所有数据都相同，使用最亮的绿色（因为这是唯一有数据的国家）
      intensity = 1.0;
      hue = 120; // 最亮的绿色
    } else {
      intensity = (views - minViews) / (maxViews - minViews);
      // 从红色(0)到绿色(120)的渐变
      hue = intensity * 120; // 0(红色) -> 120(绿色)
    }
    
    const saturation = 70; // 70%
    const lightness = 50; // 固定亮度，让颜色更鲜艳
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Convert hub stats to different data views
  const getDataForTab = (tab: string) => {
    const countryMap = new Map<string, { country: string; flag: string; views: number }>();
    
    // Country to flag emoji mapping
    const countryFlags: { [key: string]: string } = {
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Germany': '🇩🇪',
      'Australia': '🇦🇺',
      'Canada': '🇨🇦',
      'France': '🇫🇷',
      'Japan': '🇯🇵',
      'Singapore': '🇸🇬',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Switzerland': '🇨🇭',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Belgium': '🇧🇪',
      'Austria': '🇦🇹',
      'Finland': '🇫🇮',
      'Ireland': '🇮🇪',
      'New Zealand': '🇳🇿',
      'South Korea': '🇰🇷',
      'India': '🇮🇳',
      'China': '🇨🇳',
      'Brazil': '🇧🇷',
      'Mexico': '🇲🇽',
      'Argentina': '🇦🇷',
      'Chile': '🇨🇱',
      'South Africa': '🇿🇦',
      'United Arab Emirates': '🇦🇪',
      'Saudi Arabia': '🇸🇦',
      'Israel': '🇮🇱',
      'Turkey': '🇹🇷',
      'Poland': '🇵🇱',
      'Czech Republic': '🇨🇿',
      'Hungary': '🇭🇺',
      'Romania': '🇷🇴',
      'Bulgaria': '🇧🇬',
      'Croatia': '🇭🇷',
      'Slovenia': '🇸🇮',
      'Slovakia': '🇸🇰',
      'Estonia': '🇪🇪',
      'Latvia': '🇱🇻',
      'Lithuania': '🇱🇹',
      'Luxembourg': '🇱🇺',
      'Malta': '🇲🇹',
      'Cyprus': '🇨🇾',
      'Greece': '🇬🇷',
      'Portugal': '🇵🇹',
      'Iceland': '🇮🇸',
      'Liechtenstein': '🇱🇮',
      'Monaco': '🇲🇨',
      'San Marino': '🇸🇲',
      'Vatican City': '🇻🇦',
      'Andorra': '🇦🇩',
      'Malaysia': '🇲🇾',
      'Thailand': '🇹🇭',
      'Vietnam': '🇻🇳',
      'Philippines': '🇵🇭',
      'Indonesia': '🇮🇩',
      'Taiwan': '🇹🇼',
      'Hong Kong': '🇭🇰',
      'Macau': '🇲🇴',
      'Russia': '🇷🇺',
      'Ukraine': '🇺🇦',
      'Belarus': '🇧🇾',
      'Moldova': '🇲🇩',
      'Georgia': '🇬🇪',
      'Armenia': '🇦🇲',
      'Azerbaijan': '🇦🇿',
      'Kazakhstan': '🇰🇿',
      'Uzbekistan': '🇺🇿',
      'Kyrgyzstan': '🇰🇬',
      'Tajikistan': '🇹🇯',
      'Turkmenistan': '🇹🇲',
      'Mongolia': '🇲🇳',
      'North Korea': '🇰🇵',
      'Myanmar': '🇲🇲',
      'Laos': '🇱🇦',
      'Cambodia': '🇰🇭',
      'Bangladesh': '🇧🇩',
      'Sri Lanka': '🇱🇰',
      'Nepal': '🇳🇵',
      'Bhutan': '🇧🇹',
      'Maldives': '🇲🇻',
      'Pakistan': '🇵🇰',
      'Afghanistan': '🇦🇫',
      'Iran': '🇮🇷',
      'Iraq': '🇮🇶',
      'Syria': '🇸🇾',
      'Lebanon': '🇱🇧',
      'Jordan': '🇯🇴',
      'Kuwait': '🇰🇼',
      'Qatar': '🇶🇦',
      'Bahrain': '🇧🇭',
      'Oman': '🇴🇲',
      'Yemen': '🇾🇪',
      'Egypt': '🇪🇬',
      'Libya': '🇱🇾',
      'Tunisia': '🇹🇳',
      'Algeria': '🇩🇿',
      'Morocco': '🇲🇦',
      'Western Sahara': '🇪🇭',
      'Mauritania': '🇲🇷',
      'Mali': '🇲🇱',
      'Chad': '🇹🇩',
      'Sudan': '🇸🇩',
      'South Sudan': '🇸🇸',
      'Ethiopia': '🇪🇹',
      'Eritrea': '🇪🇷',
      'Djibouti': '🇩🇯',
      'Somalia': '🇸🇴',
      'Kenya': '🇰🇪',
      'Uganda': '🇺🇬',
      'Tanzania': '🇹🇿',
      'Rwanda': '🇷🇼',
      'Burundi': '🇧🇮',
      'Democratic Republic of the Congo': '🇨🇩',
      'Republic of the Congo': '🇨🇬',
      'Central African Republic': '🇨🇫',
      'Cameroon': '🇨🇲',
      'Nigeria': '🇳🇬',
      'Burkina Faso': '🇧🇫',
      'Ghana': '🇬🇭',
      'Ivory Coast': '🇨🇮',
      'Liberia': '🇱🇷',
      'Sierra Leone': '🇸🇱',
      'Guinea': '🇬🇳',
      'Guinea-Bissau': '🇬🇼',
      'Senegal': '🇸🇳',
      'Gambia': '🇬🇲',
      'Cape Verde': '🇨🇻',
      'Equatorial Guinea': '🇬🇶',
      'Gabon': '🇬🇦',
      'São Tomé and Príncipe': '🇸🇹',
      'Angola': '🇦🇴',
      'Zambia': '🇿🇲',
      'Zimbabwe': '🇿🇼',
      'Botswana': '🇧🇼',
      'Namibia': '🇳🇦',
      'Lesotho': '🇱🇸',
      'Eswatini': '🇸🇿',
      'Madagascar': '🇲🇬',
      'Comoros': '🇰🇲',
      'Mauritius': '🇲🇺',
      'Seychelles': '🇸🇨',
      'Reunion': '🇷🇪',
      'Mayotte': '🇾🇹',
      'Greenland': '🇬🇱',
      'Guatemala': '🇬🇹',
      'Belize': '🇧🇿',
      'El Salvador': '🇸🇻',
      'Honduras': '🇭🇳',
      'Nicaragua': '🇳🇮',
      'Costa Rica': '🇨🇷',
      'Panama': '🇵🇦',
      'Colombia': '🇨🇴',
      'Venezuela': '🇻🇪',
      'Guyana': '🇬🇾',
      'Suriname': '🇸🇷',
      'French Guiana': '🇬🇫',
      'Ecuador': '🇪🇨',
      'Peru': '🇵🇪',
      'Bolivia': '🇧🇴',
      'Paraguay': '🇵🇾',
      'Uruguay': '🇺🇾',
      'Falkland Islands': '🇫🇰',
      'South Georgia': '🇬🇸',
      'Antarctica': '🇦🇶'
    };

    // Aggregate hub data by country based on selected tab
    hubStats.forEach(hub => {
      const country = hub.country || 'Unknown';
      const flag = countryFlags[country] || '🌍';
      const currentData = countryMap.get(country);
      
      let value = 0;
      switch (tab) {
        case 'hubs':
          value = 1; // Count number of hubs
          break;
        case 'projects':
          value = hub.projectCount || 0;
          break;
        case 'accUsers':
          value = hub.accMemberCount || 0;
          break;
        case 'appUsers':
          value = hub.hubAppUserCount || 0;
          break;
        default:
          value = hub.views || 0;
      }
      
      if (currentData) {
        currentData.views += value;
      } else {
        countryMap.set(country, {
          country,
          flag,
          views: value
        });
      }
    });

    // Convert to array and sort by views
    return Array.from(countryMap.values())
      .sort((a, b) => b.views - a.views)
      .slice(0, 20); // Top 20 countries
  };

  const getTabTitle = (tab: string) => {
    switch (tab) {
      case 'hubs':
        return 'Hub Distribution';
      case 'projects':
        return 'Project Distribution';
      case 'accUsers':
        return 'ACC User Distribution';
      case 'appUsers':
        return 'App User Distribution';
      default:
        return 'Hub Geographic Distribution';
    }
  };

  const currentData = getDataForTab(activeTab);

  return (
          <Card className="w-full h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">{getTabTitle(activeTab)}</CardTitle>
          </div>
        </CardHeader>
              <CardContent className="pt-0 h-full pb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
            <TabsList className="grid w-full grid-cols-4 mb-4 bg-gray-100 p-1 rounded-lg">
              <TabsTrigger 
                value="hubs" 
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:font-medium"
              >
                Hubs
              </TabsTrigger>
              <TabsTrigger 
                value="projects"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:font-medium"
              >
                Projects
              </TabsTrigger>
              <TabsTrigger 
                value="accUsers"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:font-medium"
              >
                ACC Users
              </TabsTrigger>
              <TabsTrigger 
                value="appUsers"
                className="data-[state=active]:bg-white data-[state=active]:text-blue-600 data-[state=active]:shadow-sm data-[state=active]:font-medium"
              >
                App Users
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value={activeTab} className="pb-4">
              <div className="grid grid-cols-6 gap-4 h-[400px]">
                {/* World Map - Takes 4/6 of the space */}
                <div className="col-span-4 h-full overflow-hidden rounded-lg border bg-white">
                  <WorldMap data={currentData} activeTab={activeTab} />
                </div>
              
              {/* Top Countries List - Takes 2/6 of the space */}
              <div className="col-span-2 h-full overflow-y-auto bg-white rounded-lg border p-4">
                <h3 className="text-lg font-semibold mb-4 text-center">Top countries</h3>
                <div className="space-y-3">
                  {currentData.slice(0, 10).map((location, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-4 bg-gray-200 rounded flex items-center justify-center text-xs">
                        {location.flag}
                      </div>
                      <span className="flex-1 text-sm font-medium truncate text-center">{location.country}</span>
                      <div className="flex-1 min-w-0">
                        <div className="relative">
                          <div 
                            className="h-2 rounded"
                            style={{ 
                              width: `${(location.views / Math.max(...currentData.map(l => l.views))) * 100}%`,
                              backgroundColor: getBarColor(location.views, currentData)
                            }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-gray-700 w-8 text-center">
                        {location.views}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t text-center">
                  <a href="#" className="text-xs text-blue-600 hover:text-blue-800">View all</a>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default HubGeographicDistribution;
